import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import pickBy from 'lodash/pickBy';
import isNil from 'lodash/isNil';
import { useRouteMatch, useHistory } from 'react-router-dom';
import { useMount } from 'react-use';
import isEmpty from 'lodash/isEmpty';

import { Logger } from '../../../../../common/infrastructure/logger';
import {
  CustomError,
  useAppErrors,
} from '../../../../../common/contexts/app-error-context';
import Responsive, {
  useMobile,
} from '../../../../../common/components/responsive/Responsive';
import {
  ADD_FEATURE_TYPE,
  compilePath,
  FEEDBACK_WIZARD_STEP,
} from '../../../../../common/utils/helpers/routes';
import {
  formStateChanged,
  selectFormState,
  clearFormState,
  selectMarkerPositionCountryCode,
  clearRoutablePoints,
  selectCurrentFormId,
  currentFormIdChanged,
} from '../../../../../common/store/item-edit';
import { getLastSubmittedEmail } from '../../../../../common/utils/helpers/emailCache';
import {
  initialLoadingPhotosHandler,
  newPhotosHandler,
  photosReducer,
} from '../../../../../common/components/controls/photo-upload/PhotoUpload';
import Button, {
  appearances,
} from '../../../../../common/components/controls/button/Button';
import {
  EDIT_SUGGESTION_TYPES,
  EDIT_SUGGESTION_VERSION,
} from '../../../../../common/utils/constants/constants';
import { selectUserNotificationEnabled } from '../../../../../common/store/user-notificaition-settings';
import Form, {
  FormField,
} from '../../../../../common/components/forms/form/Form';
import DefaultFormFooterHint from '../../../../../common/components/forms/default-form-footer-hint/DefaultFormFooterHint';
import { FIELD_TYPES } from '../../../../../common/components/forms/field/Field';
import { validators } from '../../../../../common/utils/helpers/validators';
import {
  sendEditSuggestionAction,
  storeLastSubmittedEmailAction,
  analyticsTrackAction,
} from '../../../../../common/utils/helpers/EditSuggestionActions';

import './AddIncidentForm.scss';
import {
  getReferrer,
  getReferrerAccount,
} from '../../../../../common/utils/helpers/editSuggestions';

const INCIDENT_TYPE_OPTIONS = [
  { label: 'Congestion', value: 'congestion' },
  { label: 'Construction', value: 'construction' },
  { label: 'Disabled Vehicle', value: 'disabled_vehicle' },
  { label: 'Lane Restriction', value: 'lane_restriction' },
  { label: 'Mass Transit', value: 'mass_transit' },
  { label: 'Other News', value: 'other_news' },
  { label: 'Planned Event', value: 'planned_event' },
  { label: 'Road Closure', value: 'road_closure' },
  { label: 'Road Hazard', value: 'road_hazard' },
  { label: 'Weather', value: 'weather' },
  { label: 'Miscellaneous', value: 'miscellaneous' },
];

const getAdditionalInformationFormFields = ({
  attachmentsDispatch,
}): FormField[] => [
  {
    field: 'email',
    label: 'Your email',
    defaultValue: '',
    getPrefillValue: () => getLastSubmittedEmail(),
    required: true,
    validate: validators.compose(
      validators.required('Email'),
      validators.email,
    ),
    type: FIELD_TYPES.input,
    controlProps: {
      placeholder: 'You will get an email when changes are made',
      // name and autocomplete are required for email autocompletion done by browser
      name: 'email',
      autoComplete: 'email',
    },
  },
  {
    field: 'comment',
    label: 'Comments',
    defaultValue: '',
    type: FIELD_TYPES.textArea,
    controlProps: {
      placeholder: 'Add here some additional information',
    },
  },
  {
    field: 'attachments',
    label: 'Photos',
    defaultValue: {},
    type: FIELD_TYPES.photos,
    controlProps: { photosDispatch: attachmentsDispatch },
  },
];

const getFeedbackFormFields = ({
  incidentStartDate,
  incidentEndDate,
}): FormField[] => [
  {
    field: 'type',
    label: 'Incident Type',
    defaultValue: [],
    type: FIELD_TYPES.advancedSelect,
    required: true,
    validate: validators.validateSelected(
      validators.required('Incident Type'),
      (value) => value.join(''),
    ),
    controlProps: {
      placeholder: 'Select the type from the list',
      options: INCIDENT_TYPE_OPTIONS,
      isMulti: true,
    },
  },
  {
    field: 'description',
    label: 'Description',
    defaultValue: '',
    type: FIELD_TYPES.input,
    controlProps: {
      title: 'Describe the incident',
      placeholder: 'Describe the incident',
    },
  },
  {
    field: 'isPermanentClosure',
    label: 'Is this a permanent closure?',
    defaultValue: false,
    type: FIELD_TYPES.switch,
    checkChanged: () => false,
  },
  {
    field: 'incidentStart',
    label: 'Incident Start',
    getFormControlValue: (val) => (val ? new Date(val) : val),
    beforeChange: (val) =>
      incidentEndDate && val > incidentEndDate ? incidentEndDate : val,
    defaultValue: null,
    type: FIELD_TYPES.datepicker,
    controlProps: {
      startDate: incidentStartDate,
      endDate: incidentEndDate,
      maxDate: incidentEndDate,
      maxTime:
        incidentStartDate?.toDateString() === incidentEndDate?.toDateString()
          ? incidentEndDate
          : undefined,
      selectsStart: true,
      title: 'Set Start Date/Time',
      placeholder: 'Set Start Date/Time',
    },
  },
  {
    field: 'incidentEnd',
    label: 'Incident End',
    getFormControlValue: (val) => (val ? new Date(val) : val),
    beforeChange: (val) =>
      incidentStartDate && val < incidentStartDate ? incidentStartDate : val,
    defaultValue: null,
    type: FIELD_TYPES.datepicker,
    controlProps: {
      minDate: incidentStartDate,
      minTime:
        incidentStartDate?.toDateString() === incidentEndDate?.toDateString()
          ? incidentStartDate
          : undefined,
      startDate: incidentStartDate,
      endDate: incidentEndDate,
      selectsEnd: true,
      title: 'Set End Date/Time',
      placeholder: 'Set End Date/Time',
    },
  },
  {
    field: 'authoritativeSources',
    label: 'Source URL',
    defaultValue: '',
    type: FIELD_TYPES.input,
    controlProps: {
      title: 'Add URL to authoritative source',
      placeholder: 'Add URL to authoritative source',
    },
  },
];

const getJointFormFields = ({
  attachmentsDispatch,
  incidentStartDate,
  incidentEndDate,
}): FormField[] => [
  ...getFeedbackFormFields({ incidentStartDate, incidentEndDate }),
  ...getAdditionalInformationFormFields({ attachmentsDispatch }),
];

const FORM_ID = 'AddIncidentForm';

export interface AddIncidentFormProps {
  userLocation;
  itemDragPosition;
  formDisabled;
  onSubmit;
  onCancel;
}

interface RouteMatch {
  wizardStep;
}

const AddIncidentForm = ({
  userLocation,
  itemDragPosition,
  formDisabled,
  onSubmit,
  onCancel,
}: AddIncidentFormProps) => {
  const { showErrorPage } = useAppErrors();
  const history = useHistory();
  const match = useRouteMatch<RouteMatch>();
  const isMobile = useMobile();
  const [isLoading, setLoadingState] = useState(false);

  const notificationsEnabled = useSelector(selectUserNotificationEnabled);
  const formState = useSelector(selectFormState);
  const currentFormId = useSelector(selectCurrentFormId);
  const formStateRef = useRef<Record<string, unknown>>();
  formStateRef.current = formState;

  const dispatch = useDispatch();
  const setFormState = useCallback(
    (formState) => dispatch(formStateChanged(formState)),
    [dispatch],
  );

  // clean up other forms to keep local storage size as small as possible
  useMount(() => {
    if (currentFormId !== FORM_ID) {
      dispatch(clearFormState());
      dispatch(currentFormIdChanged(FORM_ID));
    }

    dispatch(clearRoutablePoints());
  });

  const [isFeedbackFormShown, setIsFeedbackFormShown] = useState(!!formState);
  // guarantee that feedback is always filled with all the data on mobile
  useEffect(() => {
    if (isFeedbackFormShown) {
      if (isMobile && !match.params.wizardStep) {
        history.replace({
          pathname: compilePath('add', null, {
            featureType: ADD_FEATURE_TYPE.incident,
            wizardStep: FEEDBACK_WIZARD_STEP.feedback,
          }),
          search: history.location.search,
        });
      }

      return;
    }

    if (!isMobile) {
      setIsFeedbackFormShown(true);

      return;
    }

    if (match.params.wizardStep === FEEDBACK_WIZARD_STEP.feedback) {
      setIsFeedbackFormShown(true);

      return;
    }

    if (
      !match.params.wizardStep ||
      match.params.wizardStep === FEEDBACK_WIZARD_STEP.additionalInformation
    ) {
      history.replace({
        pathname: compilePath('add', null, {
          featureType: ADD_FEATURE_TYPE.incident,
          wizardStep: FEEDBACK_WIZARD_STEP.location,
        }),
        search: history.location.search,
      });
    }
  }, [isFeedbackFormShown, isMobile, match, history]);

  const attachmentsDispatch = useCallback(
    (action) => {
      if (!formStateRef.current) {
        return;
      }

      const newAttachments = photosReducer(
        (formStateRef.current.values as Record<string, any>).attachments,
        action,
      );
      const newFormState = {
        ...formStateRef.current,
        values: {
          ...(formStateRef.current.values as Record<string, unknown>),
          attachments: newAttachments,
        },
      };
      // set ref to handle sequential actions between state updates and rerenders
      formStateRef.current = newFormState;

      setFormState(newFormState);

      return action;
    },
    [setFormState],
  );

  const attachments = useMemo(() => formState?.values.attachments, [formState]);

  // reload all still loading images, because it won't be handled anyway
  useMount(() => {
    if (attachments) {
      initialLoadingPhotosHandler(attachments, attachmentsDispatch);
    }
  });

  useEffect(() => {
    if (attachments) {
      newPhotosHandler(attachments, attachmentsDispatch);
    }
  }, [attachments, attachmentsDispatch]);

  const incidentStartDate = useMemo(
    () =>
      formState?.values?.incidentStart
        ? new Date(formState?.values?.incidentStart)
        : formState?.values?.incidentStart,
    [formState],
  );
  const incidentEndDate = useMemo(
    () =>
      formState?.values?.incidentEnd
        ? new Date(formState?.values?.incidentEnd)
        : formState?.values?.incidentEnd,
    [formState],
  );

  const jointFormFields = useMemo(
    () =>
      getJointFormFields({
        attachmentsDispatch,
        incidentStartDate,
        incidentEndDate,
      }),
    [attachmentsDispatch, incidentStartDate, incidentEndDate],
  );
  const feedbackFormFields = useMemo(
    () => getFeedbackFormFields({ incidentStartDate, incidentEndDate }),
    [incidentStartDate, incidentEndDate],
  );
  const additionalInformationFormFields = useMemo(
    () =>
      getAdditionalInformationFormFields({
        attachmentsDispatch,
      }),
    [attachmentsDispatch],
  );

  const initialValues = useMemo(
    () =>
      jointFormFields.reduce((acc, { field, defaultValue }) => {
        acc[field] = defaultValue;
        return acc;
      }, {}),
    [jointFormFields],
  );

  useEffect(() => {
    if (!formState) {
      const values = jointFormFields.reduce(
        (acc, { field, defaultValue, getPrefillValue }) => {
          acc[field] = (getPrefillValue && getPrefillValue()) || defaultValue;
          return acc;
        },
        {},
      );

      setFormState({ values });
    }
  }, [formState, setFormState, jointFormFields]);

  const handleSubmit = useCallback(
    async (values) => {
      const {
        type,
        description,
        isPermanentClosure,
        incidentStart,
        incidentEnd,
        authoritativeSources,

        email,
        comment,
        attachments,
      } = values;
      const payload = pickBy(
        {
          version: EDIT_SUGGESTION_VERSION,
          action: 'create',
          reason: 'missing_incident',
          location: itemDragPosition && {
            longitude: itemDragPosition[0],
            latitude: itemDragPosition[1],
          },
          userEmail: email,
          userLocation: !isEmpty(userLocation)
            ? {
                longitude: userLocation.longitude,
                latitude: userLocation.latitude,
              }
            : null,
          referrer: getReferrer(),
          referrerAccount: getReferrerAccount(),
          notificationsDisabled: !notificationsEnabled,
          comment,
          attachments: Object.values(attachments as any[])
            .filter(({ fileId }) => fileId)
            .map(({ fileId }) => fileId),
          changes: pickBy(
            {
              incidentTypes: type,
              description,
              isPermanent: isPermanentClosure,
              startedAt: incidentStart
                ? new Date(incidentStart).toISOString()
                : incidentStart,
              endedAt: incidentEnd
                ? new Date(incidentEnd).toISOString()
                : incidentEnd,
              impactedSegment: itemDragPosition && [
                {
                  type: 'point',
                  geometry: [
                    {
                      longitude: itemDragPosition[0],
                      latitude: itemDragPosition[1],
                    },
                  ],
                },
              ],
              authoritativeSources,
            },
            (val) => !isNil(val) && val !== '',
          ),
        },
        (val) => !isNil(val) && val !== '',
      );

      try {
        setLoadingState(true);

        const editSuggestionResult = await sendEditSuggestionAction(
          {
            editSuggestionType: EDIT_SUGGESTION_TYPES.incident,
            origin: FORM_ID,
          },
          payload,
        );

        storeLastSubmittedEmailAction(
          {
            email,
            origin: FORM_ID,
          },
          editSuggestionResult,
        );

        analyticsTrackAction({
          analyticsTrackingMessage: 'Incident Addition Submit',
          origin: FORM_ID,
          analyticsTrackParams: {
            coordinates: itemDragPosition,
            incidentTypes: type,
          },
        });

        if (onSubmit) {
          onSubmit(editSuggestionResult.result);
        }
      } catch (error) {
        const _error: CustomError = error as CustomError;

        Logger.error('Error while submitting edit suggestion', _error.message);

        showErrorPage({
          type: _error.type,
          message: _error.message,
        } as CustomError);
      } finally {
        setLoadingState(false);
      }
    },
    [
      itemDragPosition,
      notificationsEnabled,
      onSubmit,
      showErrorPage,
      userLocation,
    ],
  );

  const handleLocationFormSubmit = useCallback(
    () =>
      history.push({
        pathname: compilePath('add', null, {
          featureType: ADD_FEATURE_TYPE.incident,
          wizardStep: FEEDBACK_WIZARD_STEP.feedback,
        }),
        search: history.location.search,
      }),
    [history],
  );

  const handleFeedbackFormSubmit = useCallback(
    () =>
      history.push({
        pathname: compilePath('add', null, {
          featureType: ADD_FEATURE_TYPE.incident,
          wizardStep: FEEDBACK_WIZARD_STEP.additionalInformation,
        }),
        search: history.location.search,
      }),
    [history],
  );

  const { wizardStep } = match.params;

  const markerPositionCountryCode = useSelector(
    selectMarkerPositionCountryCode,
  );

  const outerInput = useMemo(
    () => ({ markerPositionCountryCode }),
    [markerPositionCountryCode],
  );

  return (
    <>
      <Responsive mobile={false}>
        <Form
          className="add-incident-form"
          state={formState}
          fields={jointFormFields}
          initialValues={initialValues}
          outerInput={outerInput}
          formDisabled={formDisabled || isLoading}
          onStateChange={setFormState}
          onSubmit={handleSubmit}
          onCancel={onCancel}
          footerHint={<DefaultFormFooterHint />}
        />
      </Responsive>
      <Responsive mobile={true}>
        {wizardStep === FEEDBACK_WIZARD_STEP.location && (
          <div className="add-incident-form__location-pickup-button-container">
            <Button
              appearance={appearances.PRIMARY}
              disabled={formDisabled}
              onClick={handleLocationFormSubmit}
            >
              Next
            </Button>
          </div>
        )}
        {wizardStep === FEEDBACK_WIZARD_STEP.feedback && (
          <Form
            className="add-incident-form"
            state={formState}
            fields={feedbackFormFields}
            initialValues={initialValues}
            submitLabel="Next"
            outerInput={outerInput}
            formDisabled={formDisabled}
            stepLabel="1/2"
            onStateChange={setFormState}
            onSubmit={handleFeedbackFormSubmit}
          />
        )}

        {wizardStep === FEEDBACK_WIZARD_STEP.additionalInformation && (
          <Form
            state={formState}
            fields={additionalInformationFormFields}
            initialValues={initialValues}
            formDisabled={isLoading}
            stepLabel="2/2"
            onStateChange={setFormState}
            onSubmit={handleSubmit}
            footerHint={<DefaultFormFooterHint />}
          />
        )}
      </Responsive>
    </>
  );
};

export default AddIncidentForm;
